import Chart, { useChart } from 'src/components/chart';
import { useTheme, alpha, Box, Typography } from '@mui/material';
// Localisation
import { useLocales } from "src/locales";
import { fDate } from 'src/utils/format-time';
import CiCard from './CiCard';


interface WeeklyDataPoint {
  week_start: string;
  solo_bidder: number;
  top_ad_position: number;
  organic_only: number;
  compet_only: number;
}

interface BlendedPresenceRateCardProps {
  title: string;
  dateRange: string;
  mainContentTitle?: string;
  topMetric?: string;
  weeklySeries?: WeeklyDataPoint[];
  loading?: boolean;
}

export default function BlendedPresenceRateCard(props: BlendedPresenceRateCardProps) {
  const { t } = useLocales()
  const theme = useTheme()

  // Transform weekly data into chart series format
  const weeklyData = props.weeklySeries || [];
  const organicOnlyData = weeklyData.map((d) => d.organic_only || 0);
  const soloBidderData = weeklyData.map((d) => d.solo_bidder || 0);
  const topAdPositionData = weeklyData.map((d) => d.top_ad_position || 0);
  const competitorOnlyData = weeklyData.map((d) => d.compet_only || 0);

  // Calculate current values for tooltip (from last week or average)
  const lastWeek = weeklyData[weeklyData.length - 1];
  const currentOrganicOnly = lastWeek?.organic_only || 0;
  const currentSoloBidder = lastWeek?.solo_bidder || 0;
  const currentTopAdPosition = lastWeek?.top_ad_position || 0;
  const currentCompetitorOnly = lastWeek?.compet_only || 0;
  const currentBlendedPresenceRate = currentOrganicOnly + currentSoloBidder + currentTopAdPosition;

  const series = [
    {
      name: t('dashboard.global.competitiveIntelligence.organic_only'),
      data: organicOnlyData,
      icon: 'solar:global-linear',
      color: '#18A055',
      value: currentOrganicOnly
    },
    {
      name: t('dashboard.global.competitiveIntelligence.solo_bidder'),
      data: soloBidderData,
      color: '#C09910',
      value: currentSoloBidder
    },
    {
      name: t('dashboard.global.competitiveIntelligence.top_ad_position'),
      data: topAdPositionData,
      color: '#F9DE81',
      value: currentTopAdPosition
    },
    // {
    //   name: t('dashboard.global..competitiveIntelligence.competitors_only'),
    //   data: competitorOnlyData,
    //   icon: 'ri:sword-line',
    //   color: '#F9DE81',
    //   value: currentCompetitorOnly
    // }
  ];

  const topMetric = props.topMetric || (currentBlendedPresenceRate > 0 ? `${Math.round(currentBlendedPresenceRate)}%` : "N/A")

  // Check if there's no data
  const hasNoData = weeklyData.length === 0 || currentBlendedPresenceRate === 0;

  const chartOptions = useChart({
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: true,
      },
      sparkline: {
        enabled: true,
      }
    },
    plotOptions: {
      bar: {
        columnWidth: '25%',
        borderRadius: 0,
        borderRadiusWhenStacked: 'last',
      },
    },
    fill: {
      colors: ["#18A055", "#C09910", "#F9DE81"]
    },
    colors: ["#18A055", "#C09910", "#F9DE81"],
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        if (dataPointIndex < 0 || dataPointIndex >= weeklyData.length) return '';

        const weekData = weeklyData[dataPointIndex];
        const organicValue = weekData?.organic_only || 0;
        const soloBidderValue = weekData?.solo_bidder || 0;
        const topAdPositionValue = weekData?.top_ad_position || 0;
        const competitorOnlyValue = weekData?.compet_only || 0;

        const isDark = theme.palette.mode === 'dark';
        const bgColor = isDark
          ? alpha(theme.palette.grey[800], 0.95)
          : alpha(theme.palette.background.paper, 0.95);
        const textColor = theme.palette.text.primary;
        const textSecondaryColor = theme.palette.text.secondary;
        const borderColor = theme.palette.divider;
        const fontSize = theme.typography.pxToRem(13);
        const lineHeight = 1.5;

        const formattedDate = weekData?.week_start ? fDate(weekData.week_start) : '';

        let tooltipHtml = `
        
          <div style="
            background-color: ${bgColor}; 
            color: ${textColor}; 
            font-size: ${fontSize}; 
            line-height: ${lineHeight}; 
            border: 1px solid rgba(145, 158, 171, 0.1);
            border-bottom: 1px solid rgba(145, 158, 171, 0.4);
            border-color: ${borderColor};
            box-shadow: ${isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
            padding: 12px;
            min-width: 200px;
          ">
            ${formattedDate ? `<div style="
              font-size: ${fontSize}; 
              color: ${textSecondaryColor}; 
              margin-bottom: 6px;
              line-height: ${lineHeight};
            ">
              ${formattedDate}
            </div>` : ''}
            <div style="
              font-size: ${fontSize}; 
              font-weight: 600; 
              margin-bottom: 6px;
              line-height: ${lineHeight};
            ">
              ${props.mainContentTitle ?? 'Presence breakdown'}
            </div>
            <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: 8px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" style="flex-shrink: 0;">
              <g fill="none" stroke="#18A055" stroke-width="1.5">
            <path d="M22 12a10 10 0 1 1-20.001 0A10 10 0 0 1 22 12Z"/>
            <path d="M16 12c0 1.313-.104 2.614-.305 3.827c-.2 1.213-.495 2.315-.867 3.244c-.371.929-.812 1.665-1.297 2.168c-.486.502-1.006.761-1.531.761s-1.045-.259-1.53-.761c-.486-.503-.927-1.24-1.298-2.168c-.372-.929-.667-2.03-.868-3.244A23.6 23.6 0 0 1 8 12c0-1.313.103-2.614.304-3.827s.496-2.315.868-3.244c.371-.929.812-1.665 1.297-2.168C10.955 2.26 11.475 2 12 2s1.045.259 1.53.761c.486.503.927 1.24 1.298 2.168c.372.929.667 2.03.867 3.244C15.897 9.386 16 10.687 16 12Z"/>
            <path stroke-linecap="round" d="M2 12h20"/>
            </g>
            </svg>
            <span style="font-size: ${fontSize}; line-height: ${lineHeight};">${t('dashboard.global.competitiveIntelligence.organic_only')}</span>
            </div>
            <span style="font-size: ${fontSize}; line-height: ${lineHeight}; color: ${textSecondaryColor}; margin-left: 12px;">${Math.round(organicValue)}%</span>
            </div>
              
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32" style="flex-shrink: 0;">
                    <path fill="#C09910" d="M16 4a5 5 0 1 1-5 5a5 5 0 0 1 5-5m0-2a7 7 0 1 0 7 7a7 7 0 0 0-7-7m10 28h-2v-5a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v5H6v-5a7 7 0 0 1 7-7h6a7 7 0 0 1 7 7z"/>
                  </svg>
                  <span style="font-size: ${fontSize}; line-height: ${lineHeight};">${t('dashboard.global.competitiveIntelligence.solo_bidder')}</span>
                </div>
                <span style="font-size: ${fontSize}; line-height: ${lineHeight}; color: ${textSecondaryColor}; margin-left: 12px;">${Math.round(soloBidderValue)}%</span>
              </div>
              
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32" style="flex-shrink: 0;">
                    <path fill="#F9DE81" d="M30 30h-2v-5a5.006 5.006 0 0 0-5-5v-2a7.01 7.01 0 0 1 7 7zm-8 0h-2v-5a5.006 5.006 0 0 0-5-5H9a5.006 5.006 0 0 0-5 5v5H2v-5a7.01 7.01 0 0 1 7-7h6a7.01 7.01 0 0 1 7 7zM20 2v2a5 5 0 0 1 0 10v2a7 7 0 0 0 0-14m-8 2a5 5 0 1 1-5 5a5 5 0 0 1 5-5m0-2a7 7 0 1 0 7 7a7 7 0 0 0-7-7"/>
                  </svg>
                  <span style="font-size: ${fontSize}; line-height: ${lineHeight};">${t('dashboard.global.competitiveIntelligence.top_ad_position')}</span>
                </div>
                <span style="font-size: ${fontSize}; line-height: ${lineHeight}; color: ${textSecondaryColor}; margin-left: 12px;">${Math.round(topAdPositionValue)}%</span>
              </div>
              
            </div>
          </div>
`;
        return tooltipHtml;
      }
    },
    yaxis: {
      show: false
    },
    
    xaxis: {
      labels: {
        show: false
      }
    },
    legend: {
      show: false
    },
    grid: {
      show: false
    }
  });


  function mainContent() {
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px',
            marginY: '30px',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {t('dashboard.global.no_data_available')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
            {t('dashboard.global.competitiveIntelligence.no_results_found_description')}
          </Typography>
        </Box>
      );
    }

    return (
      <Chart
        type="bar"
        series={series}
        options={chartOptions}
        sx={{ width: "100%", height: "65px" }}
        height={130} />
    )
  }

  return (
    <CiCard
      dateRange={props.dateRange}
      title={props.title}
      topMetric={topMetric}
      mainContent={mainContent()}
      loading={props.loading}
    />
  )
}
